import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Terminal, Zap, KeyRound, GitBranch, Layers, DollarSign } from "lucide-react";
import { Logo } from "@/components/Brand";
import { useAuth } from "@/context/AuthContext";

const HERO = "https://images.unsplash.com/photo-1709626011485-6fe000ea2dbc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHw0fHxhYnN0cmFjdCUyMGRhcmslMjBtb2Rlcm4lMjBnZW9tZXRyaWN8ZW58MHx8fHwxNzg2MzQ2Mzc3fDA&ixlib=rb-4.1.0&q=85";
const DEV = "https://images.unsplash.com/photo-1623479322729-28b25c16b011?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwxfHxkZXZlbG9wZXIlMjB3b3JraW5nJTIwbmlnaHR8ZW58MHx8fHwxNzg2MzQ2NDAyfDA&ixlib=rb-4.1.0&q=85";

const features = [
  { icon: Terminal, title: "Chat → App", desc: "Describe it in plain language. Catalina scaffolds a full working web app, streamed live." },
  { icon: KeyRound, title: "BYOK Vault", desc: "Bring your own keys. Encrypted per provider — Claude, GPT, Gemini, DeepSeek." },
  { icon: Zap, title: "Model Router", desc: "Auto-picks the right model per task to cut cost 60–80%, or pin one manually." },
  { icon: Layers, title: "Live Preview + Monaco", desc: "Sandboxed preview beside a real code editor. Iterate by prompt or by hand." },
  { icon: DollarSign, title: "Real-time Cost", desc: "Token-accurate spend ticks up as you generate. Per message, project and user." },
  { icon: GitBranch, title: "One-click Deploy", desc: "Ship to a public URL. GitHub push and versioning on the Cloudflare stack." },
];

export default function Landing() {
  const { user } = useAuth();
  const dest = user ? "/dashboard" : "/register";

  return (
    <div className="min-h-screen bg-background text-foreground overflow-x-hidden">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-background/60 border-b border-border/60">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Logo />
          <nav className="flex items-center gap-6">
            <Link to="/login" data-testid="nav-login" className="text-sm text-muted-foreground hover:text-white transition-colors">Sign in</Link>
            <Link to={dest} data-testid="nav-cta"
              className="text-sm font-medium bg-white text-black px-4 py-2 rounded-md hover:bg-zinc-200 transition-colors">
              Start building
            </Link>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="relative">
        <div className="absolute inset-0 grid-bg opacity-40" />
        <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 70% 30%, rgba(255,59,48,0.12), transparent 55%)" }} />
        <div className="relative max-w-6xl mx-auto px-6 pt-24 pb-20 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 border border-border rounded-full px-3 py-1 mb-6 text-xs font-mono text-muted-foreground">
              <span className="h-1.5 w-1.5 rounded-full bg-kinetic animate-pulse" /> BYOK · Multi-model · Cloudflare stack
            </motion.div>
            <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.05 }}
              className="font-display text-5xl sm:text-6xl font-extrabold leading-[0.95] tracking-tight">
              Prompt it.<br />
              <span className="text-kinetic">Ship it.</span> Own it.
            </motion.h1>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.12 }}
              className="mt-6 text-lg text-muted-foreground max-w-md leading-relaxed">
              Catalina AI turns a sentence into a working full-stack app — live preview, real code editor, and a cost meter that never lies. Bring your own keys.
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }}
              className="mt-9 flex items-center gap-4">
              <Link to={dest} data-testid="hero-cta"
                className="group inline-flex items-center gap-2 bg-white text-black font-medium px-6 py-3 rounded-md hover:bg-zinc-200 transition-colors">
                Build your first app
                <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </Link>
              <a href="#features" className="text-sm text-muted-foreground hover:text-white transition-colors">See how it works →</a>
            </motion.div>
          </div>
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.15 }}
            className="relative">
            <div className="absolute -inset-4 bg-kinetic/20 blur-3xl rounded-full" />
            <div className="relative border border-border rounded-xl overflow-hidden bg-card">
              <div className="flex items-center gap-1.5 px-4 h-9 border-b border-border">
                <span className="h-2.5 w-2.5 rounded-full bg-zinc-700" />
                <span className="h-2.5 w-2.5 rounded-full bg-zinc-700" />
                <span className="h-2.5 w-2.5 rounded-full bg-zinc-700" />
                <span className="ml-3 font-mono text-xs text-muted-foreground">catalina — builder</span>
              </div>
              <img src={HERO} alt="Catalina builder" className="w-full h-72 object-cover" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="max-w-6xl mx-auto px-6 py-20">
        <p className="font-mono text-xs text-kinetic mb-3">// what's inside</p>
        <h2 className="font-display text-4xl font-bold mb-12 max-w-xl">An AI builder that behaves like a real dev environment.</h2>
        <div className="grid md:grid-cols-3 gap-px bg-border border border-border rounded-xl overflow-hidden">
          {features.map((f, i) => (
            <motion.div key={f.title} initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.05 }}
              className="bg-card p-7 hover:bg-secondary transition-colors">
              <f.icon className="w-5 h-5 text-kinetic mb-4" />
              <h3 className="font-app text-lg font-semibold mb-2">{f.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Plans */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <div className="grid md:grid-cols-2 gap-8 items-center">
          <div className="relative border border-border rounded-xl overflow-hidden">
            <img src={DEV} alt="developer" className="w-full h-72 object-cover opacity-80" />
          </div>
          <div>
            <p className="font-mono text-xs text-kinetic mb-3">// plans & limits</p>
            <h2 className="font-display text-4xl font-bold mb-6">Standard, Pro, Gold.</h2>
            <div className="space-y-3 font-mono text-sm">
              {[
                ["standard", "3 projects · 10/day · deepseek + flash-lite"],
                ["pro", "15 projects · 50/day · claude + gemini + gpt"],
                ["gold", "100 projects · 300/day · every model"],
              ].map(([role, desc]) => (
                <div key={role} className="flex items-center justify-between border border-border rounded-lg px-4 py-3 bg-card">
                  <span className="uppercase tracking-wider text-kinetic">{role}</span>
                  <span className="text-muted-foreground text-xs">{desc}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-border mt-10">
        <div className="max-w-6xl mx-auto px-6 h-20 flex items-center justify-between">
          <Logo />
          <p className="font-mono text-xs text-muted-foreground">© 2026 Catalina AI · Cloudflare · NoCodeBackend</p>
        </div>
      </footer>
    </div>
  );
}
